/**
 * SupplierPickWinner · iter303ak-fix26 — Quote Sent · T25 · A57
 * ────────────────────────────────────────────────────────────
 * Radio-pick the winning supplier from the quotes captured in A55.
 * On save:
 *   • Sets `accepted_safety_supplier_id` on the parent appraisal
 *   • Re-runs POST /appraisals/{id}/height-safety-line with `firm`
 *     mode → REPLACES the TBC estimate row with a firm-quote row
 *     tagged import_source='height_safety_firm'.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Trophy, CheckCircle2, DollarSign, Clock, RefreshCw } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const nz = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SupplierPickWinner({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;

  const quotes = useMemo(() => {
    const raw = capturedData?.safety_supplier_quotes?.quotes || capturedData?.safety_supplier_quotes || {};
    return Object.entries(raw)
      .filter(([, q]) => q && (q.amount || q.job_ref))
      .map(([supplier_id, q]) => ({ supplier_id, ...q }));
  }, [capturedData]);

  const types = capturedData?.height_safety_types || [];

  const [pickedId, setPickedId] = useState(value?.accepted_supplier_id || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(value?.saved_at || null);
  const [err, setErr] = useState('');

  const picked = quotes.find(q => q.supplier_id === pickedId);

  const saveWinner = async () => {
    if (!parentAppraisalId || !picked) return;
    setSaving(true); setErr('');
    try {
      // 1) Stamp accepted supplier on parent
      await api.patch(`/appraisals/${parentAppraisalId}`, {
        accepted_safety_supplier_id: pickedId,
        safety_supplier_quotes: (capturedData?.safety_supplier_quotes?.quotes || capturedData?.safety_supplier_quotes || {}),
      });
      // 2) Upgrade the H&S line to firm-quote mode
      await api.post(`/appraisals/${parentAppraisalId}/height-safety-line`, {
        estimate: capturedData?.height_safety_estimate || {},
        types,
        firm: {
          supplier_name: picked.supplier_name || 'Supplier',
          job_ref: picked.job_ref || '',
          amount: picked.amount || 0,
          downtime_days: picked.downtime_days || 0,
        },
        supplier_name: picked.supplier_name || 'Supplier',
      });
      const nowIso = new Date().toISOString();
      setSavedAt(nowIso);
      if (onChange) onChange({ accepted_supplier_id: pickedId, saved_at: nowIso });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to save winner');
    } finally { setSaving(false); }
  };

  const cheapest = useMemo(() => {
    if (!quotes.length) return null;
    return [...quotes].sort((a, b) => (a.amount || 0) - (b.amount || 0))[0];
  }, [quotes]);

  return (
    <div className="space-y-4" data-testid="wf-supplier-pick-winner">
      <div className="rounded-xl border border-yellow-300 bg-yellow-50/60 p-4">
        <div className="flex items-start gap-3">
          <Trophy className="w-5 h-5 mt-0.5 shrink-0 text-yellow-700" />
          <div>
            <div className="text-sm font-semibold text-yellow-900">Pick the Winning Supplier</div>
            <div className="text-sm mt-1 text-yellow-800">
              {params.prompt || 'Choose which quote wins. This replaces the TBC H&S line on the quote with the firm amount and stamps the accepted supplier.'}
            </div>
          </div>
        </div>
      </div>

      {quotes.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No supplier quotes captured. Go back to A55 to enter at least one.
        </div>
      )}

      {quotes.length > 0 && (
        <div className="space-y-2">
          {quotes.map(q => {
            const isPicked = pickedId === q.supplier_id;
            const isCheapest = cheapest && cheapest.supplier_id === q.supplier_id && quotes.length > 1;
            return (
              <label key={q.supplier_id} className={`block p-3 border rounded-lg cursor-pointer transition ${isPicked ? 'border-yellow-500 bg-yellow-50 shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50'}`} data-testid={`wf-pick-row-${q.supplier_id}`}>
                <div className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="pick-winner"
                    checked={isPicked}
                    onChange={() => setPickedId(q.supplier_id)}
                    className="mt-1"
                    disabled={saving}
                    data-testid={`wf-pick-radio-${q.supplier_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{q.supplier_name}</span>
                      {isCheapest && (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5">Cheapest</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{q.email}</div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                      <div className="flex items-center gap-1 text-slate-700">
                        <DollarSign size={11} className="text-emerald-600" />
                        <span>Amount: <b className="font-mono">{nz(q.amount)}</b></span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-700">
                        <Clock size={11} className="text-blue-600" />
                        <span>Downtime: <b>{q.downtime_days || '—'} d</b></span>
                      </div>
                      <div className="text-slate-700">
                        <span>Ref: <span className="font-mono">{q.job_ref || '—'}</span></span>
                      </div>
                    </div>
                    {q.notes && (
                      <div className="mt-1 text-[10.5px] text-slate-500 italic">{q.notes}</div>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {pickedId && !savedAt && (
        <button
          type="button"
          onClick={saveWinner}
          disabled={saving}
          className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-40 inline-flex items-center gap-2"
          data-testid="wf-pick-save-winner"
        >
          <Trophy size={14} /> {saving ? 'Saving winner…' : 'Confirm winner & upgrade quote line'}
        </button>
      )}

      {savedAt && (
        <div className="p-3 rounded-lg border border-emerald-300 bg-emerald-50 text-sm text-emerald-800 flex items-center gap-2" data-testid="wf-pick-saved-receipt">
          <CheckCircle2 size={16} /> Winner locked in. Quote line upgraded to firm amount. Saved at {new Date(savedAt).toLocaleString()}.
        </div>
      )}

      {err && (
        <div className="p-3 rounded-lg border border-rose-300 bg-rose-50 text-sm text-rose-800 flex items-start gap-2">
          <RefreshCw size={16} className="mt-0.5" />
          <div>Failed: {err}</div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-pick-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-pick-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!savedAt}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-pick-continue"
            title={savedAt ? 'Continue' : 'Confirm the winner above first'}
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
